import { formatOpeningHours } from "@/utils/openingHours.js";
import { SHOW_HEADER_SHOP_ADDRESSES } from "@/utils/constants.js";
import { formatAddressSummary, formatHeaderShopAddress, getActiveShops, shopMapsUrl } from "@/utils/storeAddress.js";
import {
    collectDisplayedPhones,
    getGeneralStorePhones,
    hasDisplayedPhones,
    normalizeStorePhone
} from "@/utils/storePhones.js";
import "./store-contacts.css";

export function mapsSearchUrl(location) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`;
}

export function StoreOpeningHoursDetails({ openingHours, showLabel = true, className = "" }) {
    if (!openingHours) {
        return null;
    }

    const summary = formatOpeningHours(openingHours);
    if (!summary) {
        return null;
    }

    const content = (
        <>
            {showLabel && <span className="contact-label">Horaires d&apos;ouverture</span>}
            <p className="store-opening-hours-summary">{summary}</p>
            {openingHours.comment && (
                <p className="store-opening-hours-comment">{openingHours.comment}</p>
            )}
        </>
    );

    if (!showLabel) {
        return <div className={className || "contact-shop-hours"}>{content}</div>;
    }

    return <div className={`contact-item${className ? ` ${className}` : ""}`}>{content}</div>;
}

export function phoneTelUrl(phone) {
    return `tel:${phone.replace(/\s/g, "")}`;
}

export function phoneWhatsAppUrl(phone) {
    const digits = phone.replace(/\D/g, "");
    return digits ? `https://wa.me/${digits}` : null;
}

export function getStorePhones(storeInfo) {
    return getGeneralStorePhones(storeInfo);
}

function shopHasContactInfo(shop) {
    return !!(shop?.email || shop?.phones?.some((phone) => phone?.phoneNumber?.trim()));
}

export function getStoreShops(storeInfo) {
    return getActiveShops(storeInfo?.shops);
}

export function hasStoreContact(storeInfo) {
    return hasDisplayedPhones(storeInfo)
        || !!(storeInfo?.email || storeInfo?.legalAddress || getStoreShops(storeInfo).length > 0 || storeInfo?.facebookUrl)
        || getStoreShops(storeInfo).some(shopHasContactInfo);
}

export function hasHeaderStoreContact(storeInfo) {
    return hasDisplayedPhones(storeInfo)
        || !!storeInfo?.facebookUrl
        || (SHOW_HEADER_SHOP_ADDRESSES && getStoreShops(storeInfo).length > 0);
}

function PhoneIcon({ className = "" }) {
    return (
        <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path
                fill="#2563eb"
                d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 011 1V20a1 1 0 01-1 1C10.4 21 3 13.6 3 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.46.57 3.58a1 1 0 01-.25 1.02l-2.2 2.19z"
            />
        </svg>
    );
}

function WhatsAppIcon({ className = "" }) {
    return (
        <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path
                fill="#25D366"
                d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"
            />
        </svg>
    );
}

function FacebookIcon({ className = "" }) {
    return (
        <svg
            className={className}
            viewBox="0 0 24 24"
            aria-hidden="true"
            focusable="false"
        >
            <path
                fill="#1877F2"
                d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
            />
        </svg>
    );
}

function DisplayPhoneContact({ phone, shopName = null, className = "" }) {
    const normalized = normalizeStorePhone(phone);
    if (!normalized.phoneNumber) {
        return null;
    }

    const telUrl = phoneTelUrl(normalized.phoneNumber);
    const whatsAppUrl = normalized.whatsappEnabled ? phoneWhatsAppUrl(normalized.phoneNumber) : null;

    return (
        <span className={`store-phone-contact${className ? ` ${className}` : ""}`}>
            <a href={telUrl} className="store-phone-number">{normalized.phoneNumber}</a>
            <span className="store-phone-channels">
                <a href={telUrl} className="store-phone-channel" title="Appels" aria-label="Appeler">
                    <PhoneIcon className="store-phone-channel-icon" />
                </a>
                {whatsAppUrl && (
                    <a
                        href={whatsAppUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="store-phone-channel"
                        title="WhatsApp"
                        aria-label="WhatsApp"
                    >
                        <WhatsAppIcon className="store-phone-channel-icon" />
                    </a>
                )}
            </span>
            {shopName && (
                <span className="store-contact-shop-hint">({shopName})</span>
            )}
        </span>
    );
}

function HeaderShopAddressLink({ shop, className = "" }) {
    const label = formatHeaderShopAddress(shop);
    const href = shopMapsUrl(shop);
    if (!label || !href) {
        return null;
    }

    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={className}
        >
            {label}
        </a>
    );
}

function ShopAddressLink({ shop, className = "" }) {
    const summary = formatAddressSummary(shop);
    const href = shopMapsUrl(shop);
    if (!summary || !href) {
        return null;
    }

    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={className}
        >
            {shop.name ? (
                <>
                    <span className="header-shop-name">{shop.name}</span>
                    <span className="muted"> {summary}</span>
                </>
            ) : summary}
        </a>
    );
}

export function HeaderStoreContacts({ storeInfo }) {
    const displayedPhones = collectDisplayedPhones(storeInfo);
    const shops = getStoreShops(storeInfo);
    const hasInfoColumn = displayedPhones.length > 0 || storeInfo?.facebookUrl;
    const hasShopsColumn = SHOW_HEADER_SHOP_ADDRESSES && shops.length > 0;

    if (!hasInfoColumn && !hasShopsColumn) {
        return null;
    }

    return (
        <div className={`header-contacts${SHOW_HEADER_SHOP_ADDRESSES ? " header-contacts--with-shop-addresses" : ""}`}>
            <div className="header-contacts-layout">
                {hasInfoColumn && (
                    <div className="header-contacts-column header-contacts-column--info">
                        {displayedPhones.map((phone) => (
                            <div key={phone.key} className="header-contacts-line">
                                <DisplayPhoneContact phone={phone} />
                            </div>
                        ))}
                        {storeInfo.facebookUrl && (
                            <div className="header-contacts-line">
                                <a
                                    href={storeInfo.facebookUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="header-contact-facebook"
                                >
                                    Facebook
                                    <FacebookIcon className="header-contact-facebook-icon" />
                                </a>
                            </div>
                        )}
                    </div>
                )}
                {hasShopsColumn && (
                    <div className="header-contacts-column header-contacts-column--shops">
                        {shops.map((shop) => (
                            <div key={shop.id} className="header-contacts-line">
                                <HeaderShopAddressLink shop={shop} className="header-contact-location" />
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export function ContactGeneralInfo({ storeInfo }) {
    const phones = getStorePhones(storeInfo);
    const hasGeneralInfo = phones.length > 0 || storeInfo?.email || storeInfo?.facebookUrl;

    if (!hasGeneralInfo) {
        return null;
    }

    return (
        <div className="contact-general-info">
            {phones.length > 0 && (
                <div className="contact-item">
                    <span className="contact-label">
                        {phones.length > 1 ? "Téléphones généraux" : "Téléphone général"}
                    </span>
                    <div className="contact-phone-list">
                        {phones.map((phone) => {
                            const normalized = normalizeStorePhone(phone);

                            return (
                                <div key={normalized.phoneNumber} className="contact-phone-entry">
                                    <DisplayPhoneContact phone={normalized} />
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
            {storeInfo?.email && (
                <div className="contact-item">
                    <span className="contact-label">E-mail</span>
                    <a href={`mailto:${storeInfo.email}`}>{storeInfo.email}</a>
                </div>
            )}
            {storeInfo?.facebookUrl && (
                <div className="contact-item">
                    <span className="contact-label">Facebook</span>
                    <a
                        href={storeInfo.facebookUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="contact-facebook-link"
                    >
                        Voir notre page Facebook
                        <FacebookIcon className="contact-facebook-icon" />
                    </a>
                </div>
            )}
        </div>
    );
}

export function ContactShopsInfo({ storeInfo }) {
    const shops = getStoreShops(storeInfo);

    if (shops.length === 0) {
        return null;
    }

    return (
        <div className="contact-shops-info">
            <div className="contact-item">
                <span className="contact-label">
                    {shops.length > 1 ? "Nos magasins" : "Notre magasin"}
                </span>
                <div className="contact-shop-list">
                    {shops.map((shop) => (
                        <fieldset key={shop.id} className="contact-shop-entry">
                            <legend>{shop.name?.trim() || "Magasin"}</legend>
                            <a
                                href={shopMapsUrl(shop)}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {formatAddressSummary(shop)}
                            </a>
                            {shop.phones?.map((phone, index) => (
                                <div key={`${shop.id}-phone-${index}`} className="contact-shop-phone-entry">
                                    <DisplayPhoneContact phone={phone} />
                                </div>
                            ))}
                            {shop.email && (
                                <a href={`mailto:${shop.email}`}>{shop.email}</a>
                            )}
                            <StoreOpeningHoursDetails openingHours={shop.openingHours} showLabel={false} />
                        </fieldset>
                    ))}
                </div>
            </div>
        </div>
    );
}

export function ContactStoreDetails({ storeInfo }) {
    const phones = getStorePhones(storeInfo);
    const shops = getStoreShops(storeInfo);
    const hasGeneralInfo = phones.length > 0 || storeInfo?.email || storeInfo?.facebookUrl;
    const hasShops = shops.length > 0;
    const useTwoColumns = hasGeneralInfo && hasShops;

    return (
        <div className={useTwoColumns ? "contact-layout" : "contact-layout contact-layout--single"}>
            <div className="contact-layout-column contact-layout-column--general">
                <ContactGeneralInfo storeInfo={storeInfo} />
            </div>
            <div className="contact-layout-column contact-layout-column--shops">
                <ContactShopsInfo storeInfo={storeInfo} />
            </div>
        </div>
    );
}

export function FooterStoreContacts({ storeInfo }) {
    const generalPhones = getGeneralStorePhones(storeInfo);
    const shops = getStoreShops(storeInfo);
    const hasShops = shops.length > 0;
    const hasGeneralInfo = generalPhones.length > 0 || storeInfo?.email || storeInfo?.facebookUrl;

    if (!hasShops && !hasGeneralInfo) {
        return null;
    }

    return (
        <div
            className={
                hasShops && hasGeneralInfo ? "footer-content" : "footer-content footer-content--single"
            }
        >
            {hasShops && (
                <div className="footer-shops-column">
                    <span className="footer-section-label">
                        {shops.length > 1 ? "Nos magasins" : "Notre magasin"}
                    </span>
                    <div className="footer-shop-list">
                        {shops.map((shop) => (
                            <div key={shop.id} className="footer-shop-entry">
                                <ShopAddressLink shop={shop} />
                                {shop.phones?.map((phone, index) => (
                                    <div key={`${shop.id}-phone-${index}`} className="footer-shop-phone-entry">
                                        <DisplayPhoneContact phone={phone} />
                                    </div>
                                ))}
                                {shop.email && (
                                    <a href={`mailto:${shop.email}`}>{shop.email}</a>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {hasGeneralInfo && (
                <div className="footer-info-column">
                    <span className="footer-section-label">Contact général</span>
                    {generalPhones.map((phone) => (
                        <DisplayPhoneContact key={phone.phoneNumber} phone={phone} />
                    ))}
                    {storeInfo.email && (
                        <a href={`mailto:${storeInfo.email}`}>{storeInfo.email}</a>
                    )}
                    {storeInfo.facebookUrl && (
                        <a
                            href={storeInfo.facebookUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="footer-contact-facebook"
                        >
                            Facebook
                            <FacebookIcon className="footer-contact-facebook-icon" />
                        </a>
                    )}
                </div>
            )}
        </div>
    );
}
